import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { ProductBadge } from './product-badge';
import { stretchedLinkSx } from '../layout/stretched-link';
import { isBrowserHandledClick } from './browser-click';

import type { ProductDirectoryEntry } from './product-directory-api';

type ProductRowProps = {
  product: ProductDirectoryEntry;
  isCurrent: boolean;
  isFavorite: boolean;
  onToggleFavorite: (product: string) => void;
  onLaunch: (product: ProductDirectoryEntry) => void;
  /** Set by the host: the panel nests these under its own heading, the page and the widget under theirs. */
  headingLevel: 'h2' | 'h3' | 'h4';
};

function ProductRow({
  product,
  isCurrent,
  isFavorite,
  onToggleFavorite,
  onLaunch,
  headingLevel,
}: ProductRowProps) {
  const canLaunch = typeof product.url === 'string' && product.url.length > 0;
  // A star that is set is state worth seeing; an unset one is an action, offered on approach.
  let toggleOpacity = 0;
  if (isFavorite) {
    toggleOpacity = 1;
  }

  function handleLaunch(event: React.MouseEvent<HTMLAnchorElement>) {
    if (!isBrowserHandledClick(event)) {
      event.preventDefault();
      onLaunch(product);
    }
  }

  // Every row carries the same box and a transparent rule, so selecting one repaints it
  // rather than moving it.
  let rowSx: Record<string, unknown> = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    mx: -1,
    py: 1.25,
    pl: 1,
    pr: 5,
    borderLeft: '3px solid transparent',
    '&:hover [data-favorite-toggle]': { opacity: 1 },
  };
  if (isCurrent) {
    rowSx = { ...rowSx, borderLeftColor: 'primary.main' };
  }

  return (
    <Box data-current-product={isCurrent || undefined} sx={rowSx}>
      <ProductBadge badge={product.badge} category={product.category} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          component={headingLevel}
          sx={{ fontWeight: 600, color: canLaunch ? 'text.primary' : 'text.secondary' }}
        >
          {canLaunch ? (
            <Box
              component="a"
              href={product.url as string}
              onClick={handleLaunch}
              aria-current={isCurrent ? 'true' : undefined}
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                ...stretchedLinkSx({ hoverFill: true }),
              }}
            >
              {product.label}
            </Box>
          ) : (
            product.label
          )}
        </Typography>
        <Typography variant="caption" component="p" sx={{ color: 'text.secondary', display: 'block', m: 0 }}>
          {product.description}
        </Typography>
      </Box>
      <IconButton
        size="small"
        data-favorite-toggle
        aria-label={`Favorite ${product.label}`}
        aria-pressed={isFavorite}
        onClick={() => onToggleFavorite(product.name)}
        sx={{
          position: 'absolute',
          top: 6,
          right: 0,
          zIndex: 1,
          opacity: toggleOpacity,
          transition: 'opacity 150ms',
          color: isFavorite ? 'primary.main' : 'text.secondary',
          '&:focus-visible': { opacity: 1 },
          '@media (hover: none)': { opacity: 1 },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
}

export { ProductRow };
export type { ProductRowProps };
